<template>
  <!-- Page Header -->
  <PageHeader title="Mes remises et promotion" class="mb-4" />
  <div class="w-full md:px-6 ">

    <!-- Section 1: Remise sur l'ensemble de la boutique -->
    <DiscountSection
      :discounts="storeDiscounts"
      :is-loading="isLoading"
      :error="error"
      section-title="Remise sur l'ensemble de la boutique"
      button-label="Ajouter une remise"
      empty-message="Vous n'avez aucune remise."
      :columns="storeDiscountColumns"
      @add="openAddDiscountModal('store')"
      @retry="fetchDiscounts"
      @delete="handleDeleteDiscount"
      @toggle-status="handleToggleDiscountStatus"
    />

    <!-- Section 2: Remise excluant les nouveautés -->
    <DiscountSection
      :discounts="excludeDiscounts"
      :is-loading="false"
      :error="null"
      section-title="Remise excluant les nouveautés"
      button-label="Ajouter une remise"
      empty-message="vous n'avez aucune remise."
      :columns="storeDiscountColumns"
      @add="openAddDiscountModal('exclude')"
      @retry="fetchDiscounts"
      @delete="handleDeleteDiscount"
      @toggle-status="handleToggleDiscountStatus"
    />

    <!-- Section 3: Pourcentage de promotion globale (Style ancien PHP) -->
    <div class="mb-8 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-black">Appliquer une promotion globale aux produits</h3>
      </div>
      
      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Left side: Form -->
          <div class="space-y-6">
            <div class="flex items-center gap-4">
              <label class="text-black font-medium whitespace-nowrap">Pourcentage de promotion :</label>
              <div class="relative w-32">
                <input
                  v-model="globalPromotionPercent"
                  type="number"
                  min="0"
                  max="100"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                  placeholder="0"
                  :disabled="isApplyingPromotion"
                />
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-black">%</span>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <div class="flex items-center h-5">
                <input
                  id="applyToDiscounted"
                  v-model="applyToAlreadyDiscounted"
                  type="checkbox"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  :disabled="isApplyingPromotion"
                />
              </div>
              <label for="applyToDiscounted" class="text-sm text-black cursor-pointer select-none">
                Appliquer la promotion aussi aux produits déjà remisés
              </label>
            </div>

            <div class="flex flex-wrap gap-4 pt-2">
              <button
                v-if="!isGlobalPromotionActive"
                @click="applyGlobalPromotionAction"
                class="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50"
                :disabled="isApplyingPromotion || !globalPromotionPercent || globalPromotionPercent <= 0"
              >
                <i v-if="isApplyingPromotion" class="fas fa-spinner fa-spin mr-2"></i>
                <i v-else class="fas fa-check mr-2"></i>
                Appliquer la promotion
              </button>
              
              <button
                @click="handleResetAllPromotions"
                class="inline-flex items-center px-6 py-2.5 bg-white text-red-600 border border-red-200 font-medium rounded-lg hover:bg-red-50 transition-all shadow-sm disabled:opacity-50"
                :disabled="isApplyingPromotion"
              >
                <i v-if="isApplyingPromotion" class="fas fa-spinner fa-spin mr-2"></i>
                <i v-else class="fas fa-trash-alt mr-2"></i>
                Supprimer la promotion globale
              </button>
            </div>

            <!-- Feedback Message -->
            <transition name="fade">
              <div v-if="lastAppliedMessage" class="p-3 bg-green-50 text-green-700 rounded-md border border-green-100 flex items-center gap-2">
                <i class="fas fa-info-circle"></i>
                <span class="text-sm font-medium">{{ lastAppliedMessage }}</span>
              </div>
            </transition>
          </div>

          <!-- Right side: Note -->
          <div class="bg-blue-50 rounded-lg p-5 border border-blue-100 h-fit">
            <div class="flex gap-3 text-blue-800 mb-2">
              <i class="fas fa-lightbulb mt-1"></i>
              <span class="font-bold uppercase text-xs tracking-wider">Note Importante</span>
            </div>
            <p class="text-sm text-blue-700 leading-relaxed">
              Cette promotion globale met à jour directement le <strong>prix réduit</strong> de vos produits. 
              Les "remises et promotion" sont visibles dans le catalogue pour vos clients.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Discount Modal -->
    <AddDiscountModal
      :is-visible="showAddModal"
      :title="modalTitle"
      :discount-data="newDiscount"
      :validation-errors="validationErrors"
      :submitting="isSubmitting"
      @update:is-visible="showAddModal = $event"
      @submit="handleAddDiscount"
      @validate="handleValidation"
    />

    <!-- Modal de confirmation de suppression -->
    <ModalComp
      :is-visible="showDeleteModal"
      title="Confirmer la suppression"
      size="md"
      @update:is-visible="showDeleteModal = false"
    >
      <template #body>
        <div class="text-center py-4">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <i class="fas fa-exclamation-triangle text-red-600 text-xl"></i>
          </div>
          <h3 class="text-lg font-medium text-black mb-2">
            Supprimer la remise
          </h3>
          <p class="text-sm text-black mb-4">
            Êtes-vous sûr de vouloir supprimer la remise <strong>"{{ discountToDelete?.libelle }}"</strong> ?
          </p>
          <p class="text-xs text-black">
            Cette action est irréversible.
          </p>
        </div>
      </template>
      
      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="showDeleteModal = false"
            type="button"
            class="px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
          >
            Annuler
          </button>
          <button
            @click="confirmDelete"
            type="button"
            class="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
          >
            Supprimer
          </button>
        </div>
      </template>
    </ModalComp>

    <!-- Modal de confirmation de suppression de toutes les promotions -->
    <ModalComp
      :is-visible="showResetModal"
      title="Supprimer toutes les promotions ?"
      size="md"
      @update:is-visible="showResetModal = false"
    >
      <template #default>
        <div class="text-center p-4">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <i class="fas fa-trash-alt text-red-600 text-xl"></i>
          </div>
          <h3 class="text-lg font-medium text-black mb-2">
            Supprimer les promotions
          </h3>
          <p class="text-sm text-black mb-4">
            Êtes-vous sûr de vouloir supprimer <strong>toutes</strong> les promotions (globales et individuelles) appliquées à vos produits ?
          </p>
          <p class="text-xs text-black italic">
            Cette action réinitialisera les prix de tous vos produits à leur prix d'origine.
          </p>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="showResetModal = false"
            type="button"
            class="px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
          >
            Annuler
          </button>
          <button
            @click="confirmResetAllPromotions"
            type="button"
            class="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
          >
            Confirmer
          </button>
        </div>
      </template>
    </ModalComp>

    <!-- Modal de confirmation d'application/annulation de remise -->
    <ModalComp
      :is-visible="showToggleModal"
      :title="toggleModalTitle"
      size="md"
      @update:is-visible="showToggleModal = false"
    >
      <template #body>
        <div class="text-center py-4">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <i class="fas fa-check text-blue-600 text-xl"></i>
          </div>
          <h3 class="text-lg font-medium text-black mb-2">
            {{ toggleModalTitle }}
          </h3>
          <p class="text-sm text-black">
            {{ toggleModalMessage }}
          </p>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="showToggleModal = false"
            type="button"
            class="px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
          >
            Annuler
          </button>
          <button
            @click="confirmToggle"
            type="button"
            class="px-4 py-2 text-white rounded-md focus:outline-none transition-colors"
            :class="discountToToggle?.actif === 'activé' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'"
          >
            {{ discountToToggle?.actif === 'activé' ? 'Désactiver' : 'Appliquer' }}
          </button>
        </div>
      </template>
    </ModalComp>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, readonly } from 'vue'
import { PageHeader, ModalComp } from '@/components'
import DiscountSection from '@/components/discount/DiscountSection.vue'
import AddDiscountModal from '@/components/discount/AddDiscountModal.vue'
import { getRemises, createRemise, updateRemise, deleteRemise } from '@/api/discounts'
import { applyGlobalPromotion, getProductsPage } from '@/api/produits'
import { Discount, RemiseApi } from '@/types/discount'
import { useAuthStore } from '@/stores/auth'
import {useRouter} from "vue-router";
import { useNotification } from '@/composables/useNotification'
const authStore = useAuthStore()
const router = useRouter()
const { success: notifySuccess, error: notifyError } = useNotification()

const globalPromotionPercent = ref<number | null>(0)
const isGlobalPromotionActive = ref(false)
const isApplyingPromotion = ref(false)
const applyToAlreadyDiscounted = ref(false)
const lastAppliedMessage = ref('')

// Logic to determine if global promotion is active on mount
const checkInitialGlobalPromotion = async () => {
  const vendeurId = authStore.user?.id_vendeur
  if (!vendeurId) return

  try {
    // We fetch more products to be more accurate (50 samples)
    const result = await getProductsPage({
      id_vendeur: vendeurId,
      take: 50,
      skip: 0
    })

    if (result.items && result.items.length > 0) {
      const productsWithPromo = result.items.filter(p => p.prixReduit != null && p.prixReduit > 0 && p.prix && p.prix > 0)
      
      if (productsWithPromo.length > 0) {
        // Count occurrences of each percentage to find the most common one (mode)
        const counts: Record<number, number> = {}
        productsWithPromo.forEach(p => {
          if (p.prix && p.prixReduit) {
            const pct = Math.round((1 - p.prixReduit / p.prix) * 100)
            counts[pct] = (counts[pct] || 0) + 1
          }
        })

        let maxCount = 0
        let modePercent = 0
        for (const pct in counts) {
          if (counts[pct] > maxCount) {
            maxCount = counts[pct]
            modePercent = parseInt(pct)
          }
        }

        // If a significant portion of products has this discount, show it
        if (maxCount >= 1) {
          isGlobalPromotionActive.value = true
          globalPromotionPercent.value = modePercent
          lastAppliedMessage.value = `Promotion de ${modePercent}% détectée et appliquée.`
        } else {
          isGlobalPromotionActive.value = false
          globalPromotionPercent.value = null
        }
      } else {
        isGlobalPromotionActive.value = false
        globalPromotionPercent.value = null
      }
    } else {
      isGlobalPromotionActive.value = false
      globalPromotionPercent.value = null
    }
  } catch (err) {
    console.error('Error checking global promotion state:', err)
  }
}

const applyGlobalPromotionAction = async () => {
  if (globalPromotionPercent.value === null) return

  if (globalPromotionPercent.value < 0) {
    notifyError('La promotion ne peut pas être négative.')
    return
  }

  if (globalPromotionPercent.value > 100) {
    notifyError('La promotion ne peut pas dépasser 100%.')
    return
  }
  
  isApplyingPromotion.value = true
  lastAppliedMessage.value = ''
  try {
    const vendeurId = authStore.user?.id_vendeur
    if (!vendeurId) throw new Error('Vendeur non connecté')
    
    const success = await applyGlobalPromotion(
      vendeurId, 
      globalPromotionPercent.value,
      applyToAlreadyDiscounted.value
    )
    if (success) {
      isGlobalPromotionActive.value = true
      lastAppliedMessage.value = `Promotion de ${globalPromotionPercent.value}% appliquée avec succès.`
      notifySuccess('Promotion appliquée avec succès à tous les produits.')
    } else {
      notifyError('Erreur lors de l\'application de la promotion.')
    }
  } catch (err) {
    notifyError('Erreur lors de l\'application de la promotion.')
  } finally {
    isApplyingPromotion.value = false
  }
}

const handleToggleGlobalPromotion = async () => {
  const newValue = !isGlobalPromotionActive.value
  
  if (newValue && (globalPromotionPercent.value === null || globalPromotionPercent.value <= 0)) {
    notifyError('Veuillez saisir un pourcentage avant d\'activer la promotion.')
    return
  }

  if (newValue && globalPromotionPercent.value > 100) {
    notifyError('La promotion ne peut pas dépasser 100%.')
    return
  }

  isApplyingPromotion.value = true
  lastAppliedMessage.value = ''
  try {
    const vendeurId = authStore.user?.id_vendeur
    if (!vendeurId) throw new Error('Vendeur non connecté')
    
    const percent = newValue ? (globalPromotionPercent.value || 0) : 0
    const success = await applyGlobalPromotion(
      vendeurId, 
      percent,
      newValue ? applyToAlreadyDiscounted.value : true // If deactivating, we reset everything anyway
    )
    
    if (success) {
      isGlobalPromotionActive.value = newValue
      if (newValue) {
        lastAppliedMessage.value = `Promotion de ${globalPromotionPercent.value}% activée.`
      } else {
        lastAppliedMessage.value = 'Promotion désactivée (prix réinitialisés).'
      }
      notifySuccess(newValue ? 'Promotion activée.' : 'Promotion désactivée.')
    } else {
      notifyError('Erreur lors du changement de statut de la promotion.')
    }
  } catch (err) {
    notifyError('Erreur lors du changement de statut de la promotion.')
  } finally {
    isApplyingPromotion.value = false
  }
}

const handleResetAllPromotions = () => {
  showResetModal.value = true
}

const confirmResetAllPromotions = async () => {
  showResetModal.value = false
  isApplyingPromotion.value = true
  lastAppliedMessage.value = ''
  try {
    const vendeurId = authStore.user?.id_vendeur
    if (!vendeurId) throw new Error('Vendeur non connecté')
    
    // Applying 0% is equivalent to resetting ALL promotions.
    const success = await applyGlobalPromotion(vendeurId, 0, true)
    
    if (success) {
      isGlobalPromotionActive.value = false
      globalPromotionPercent.value = 0
      lastAppliedMessage.value = 'Les promotions globales ont été supprimées (tous les prix ont été réinitialisés).'
      notifySuccess('Les promotions globales et individuelles ont été supprimées.')
    } else {
      notifyError('Erreur lors de la suppression des promotions.')
    }
  } catch (err) {
    notifyError('Erreur lors de la suppression des promotions.')
  } finally {
    isApplyingPromotion.value = false
  }
}

// ===== COMPOSABLES / LOGIQUE MÉTIER =====
const useDiscountStore = () => {
  const storeDiscounts = ref<Discount[]>([]) // Remises sur l'ensemble de la boutique
  const excludeDiscounts = ref<Discount[]>([]) // Remises excluant les nouveautés
  const isLoading = ref(false)
  const error = ref<string | null>(null)


  // Actions
  const fetchDiscounts = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const vendeurId = authStore.user?.id_vendeur
      
      if (!vendeurId) {
        error.value = 'Vendeur non connecté'
        return
      }
      
      const allRemises = await getRemises(vendeurId)
      
      // Fonction pour mapper les données
      const mapRemise = (remise: RemiseApi): Discount => ({
        id: remise.id_remise.toString(),
        libelle: remise.titre || 'Remise sans titre',
        montantMin: remise.montant_min || 0,
        montantMax: remise.montant_max || 0,
        dateDebut: remise.date_debut && remise.date_debut !== '0000-00-00 00:00:00' ? new Date(remise.date_debut).toLocaleDateString('fr-FR', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        }) : '',
        dateFin: remise.date_fin && remise.date_fin !== '0000-00-00 00:00:00' ? new Date(remise.date_fin).toLocaleDateString('fr-FR', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        }) : '',
        taux: remise.montant ?? 0,
        actif: remise.actif === 1 ? 'activé' : 'désactivé'
      })
      
      storeDiscounts.value = allRemises
        .filter(remise => remise.id_remise_type !== 6)
        .map(mapRemise)
      excludeDiscounts.value = allRemises
        .filter(remise => remise.id_remise_type === 6)
        .map(mapRemise)
        
    } catch (err) {
      error.value = 'Erreur lors du chargement des remises'
    } finally {
      isLoading.value = false
    }
  }

  const addDiscount = async (discountData: Omit<Discount, 'id'>, type: 'store' | 'exclude') => {
    const vendeurId = authStore.user?.id_vendeur
    
    if (!vendeurId) {
      throw new Error('Vendeur non connecté')
    }
    
    const input = {
      id_remise_type: type === 'store' ? 1 : 6, // Type 1: boutique, Type 6: excluant nouveautés
      id_vendeur: vendeurId,
      titre: discountData.libelle,
      montant_min: discountData.montantMin,
      montant_max: discountData.montantMax,
      montant: discountData.taux,
      date_debut: discountData.dateDebut,
      date_fin: discountData.dateFin,
      pourcentage: discountData.taux,
      type: 'pourcentage',
      actif: discountData.actif === 'activé' ? 1 : 0
    }
    
    const result = await createRemise(input)
    
    // Ajouter la nouvelle remise à la liste appropriée
    const newDiscount: Discount = {
      id: result.id_remise.toString(),
      libelle: result.titre || 'Remise sans titre',
      montantMin: result.montant_min || 0,
      montantMax: result.montant_max || 0,
      dateDebut: result.date_debut && result.date_debut !== '0000-00-00 00:00:00' ? new Date(result.date_debut).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      }) : '',
      dateFin: result.date_fin && result.date_fin !== '0000-00-00 00:00:00' ? new Date(result.date_fin).toLocaleDateString('fr-FR', {
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      }) : '',
      taux: result.montant ?? 0,
      actif: result.actif === 1 ? 'activé' : 'désactivé'
    }
    
    // Ajouter à la bonne liste selon le type
    if (type === 'store') {
      storeDiscounts.value.push(newDiscount)
    } else {
      excludeDiscounts.value.push(newDiscount)
    }
    
    return newDiscount
  }

  const deleteDiscount = async (id: string) => {
    await deleteRemise(parseInt(id))
    
    // Supprimer de la liste locale appropriée
    storeDiscounts.value = storeDiscounts.value.filter(d => d.id !== id)
    excludeDiscounts.value = excludeDiscounts.value.filter(d => d.id !== id)
  }

  const toggleDiscountStatus = async (id: string) => {
    // Chercher dans les deux listes
    let discount = storeDiscounts.value.find(d => d.id === id)
    if (!discount) {
      discount = excludeDiscounts.value.find(d => d.id === id)
    }
    
    if (!discount) return
    
    const newStatus = discount.actif === 'activé' ? 0 : 1
    
    const input = {
      id_remise: parseInt(id),
      actif: newStatus
    }
    
    await updateRemise(input)
    
    // Mettre à jour le statut local
    discount.actif = newStatus === 1 ? 'activé' : 'désactivé'
  }

  return {
    // State
    storeDiscounts,
    excludeDiscounts,
    isLoading: readonly(isLoading),
    error,
    
    // Actions
    fetchDiscounts,
    addDiscount,
    deleteDiscount,
    toggleDiscountStatus
  }
}

// ===== COMPOSANT VUE =====
// Store
const {
  storeDiscounts,
  excludeDiscounts,
  isLoading,
  error,
  fetchDiscounts,
  addDiscount,
  deleteDiscount,
  toggleDiscountStatus
} = useDiscountStore()

// UI State
const showAddModal = ref(false)
const modalType = ref<'store' | 'exclude'>('store')
const showDeleteModal = ref(false)
const showResetModal = ref(false)
const discountToDelete = ref<Discount | null>(null)
const isSubmitting = ref(false)
const showToggleModal = ref(false)
const discountToToggle = ref<Discount | null>(null)

// Form data
const newDiscount = reactive({
  libelle: '',
  montantMin: '',
  montantMax: '',
  dateDebut: '',
  dateFin: '',
  taux: '',
  actif: 'activé'
})

// Computed
const modalTitle = computed(() => {
  return modalType.value === 'store' 
    ? 'Ajouter une remise sur l\'ensemble de la boutique'
    : 'Ajouter une remise excluant les nouveautés'
})

const toggleModalTitle = computed(() => {
  const isActive = discountToToggle.value?.actif === 'activé'
  return isActive ? 'Annuler la remise ?' : 'Appliquer la remise ?'
})

const toggleModalMessage = computed(() => {
  const name = discountToToggle.value?.libelle || 'cette remise'
  const isActive = discountToToggle.value?.actif === 'activé'
  return isActive
    ? `Voulez-vous annuler ${name} ?`
    : `Voulez-vous appliquer ${name} ?`
})

// Messages d'erreur
const validationErrors = ref({
  montantMin: '',
  montantMax: '',
  taux: '',
  dateRange: ''
})

// Fonction de validation pour le composant modal
const handleValidation = (errors: any) => {
  Object.assign(validationErrors.value, errors)
}

// Colonnes du tableau
const storeDiscountColumns = [
  { key: 'libelle', title: 'Libellé', sortable: true, align: 'left' as const },
  { key: 'montantMin', title: 'Montant minimum', sortable: true, align: 'left' as const },
  { key: 'montantMax', title: 'Montant maximum', sortable: true, align: 'left' as const },
  { key: 'dateDebut', title: 'Date Debut', sortable: true, align: 'left' as const },
  { key: 'dateFin', title: 'Date Fin', sortable: true, align: 'left' as const },
  { key: 'taux', title: 'Taux de remise', sortable: true, align: 'left' as const, formatter: (value: number) => value != null ? `${value}%` : '0%' }
]

// ===== MÉTHODES UI =====
const openAddDiscountModal = (type: 'store' | 'exclude') => {
  modalType.value = type
  showAddModal.value = true
  resetForm()
}

const resetForm = () => {
  newDiscount.libelle = ''
  newDiscount.montantMin = ''
  newDiscount.montantMax = ''
  newDiscount.dateDebut = ''
  newDiscount.dateFin = ''
  newDiscount.taux = ''
  newDiscount.actif = 'activé'
}

const handleAddDiscount = async (formData?: {
  libelle: string
  montantMin: string
  montantMax: string
  dateDebut: string
  dateFin: string
  taux: string
  actif: string
}) => {
  // Éviter les doubles clics si une soumission est déjà en cours
  if (isSubmitting.value) return
  // Utiliser les données envoyées par la modal si présentes，否则回退到本地状态
  const src = formData ?? newDiscount

  // Validation des champs requis (montantMin et montantMax sont optionnels)
  if (!src.libelle || !src.dateDebut || !src.dateFin || !src.taux) {
    // Ne pas utiliser l'état d'erreur global (réservé au chargement)
    notifyError('Veuillez remplir tous les champs obligatoires')
    return
  }

  // Validation des dates
  if (new Date(src.dateFin) <= new Date(src.dateDebut)) {
    notifyError('La date de fin doit être postérieure à la date de début')
    return
  }

  // Prévenir les doublons de libellé (insensible à la casse et aux espaces)
  const name = src.libelle.trim().toLowerCase()
  if (name) {
    const list = modalType.value === 'store' ? storeDiscounts.value : excludeDiscounts.value
    const exists = list.some(d => (d.libelle || '').trim().toLowerCase() === name)
    if (exists) {
      notifyError('Une remise avec ce libellé existe déjà.')
      return
    }
  }

  const discountData = {
    libelle: src.libelle.trim(),
    montantMin: src.montantMin ? parseFloat(src.montantMin) : 0,
    montantMax: src.montantMax ? parseFloat(src.montantMax) : 999999,
    dateDebut: src.dateDebut,
    dateFin: src.dateFin,
    // GraphQL attend un Int pour "pourcentage" -> forcer un entier
    taux: Number.parseInt(String(src.taux), 10),
    actif: (src.actif as 'activé' | 'désactivé') || 'activé'
  }

  isSubmitting.value = true
  try {
    await addDiscount(discountData, modalType.value)
    showAddModal.value = false
    resetForm()
    // Notification de succès (toast vert)
    notifySuccess('Remise ajoutée avec succès')
  } catch (e) {
    // laisser le message d'erreur géré plus haut si besoin
    notifyError('Échec de l\'ajout de la remise')
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteDiscount = (row: Discount) => {
  discountToDelete.value = row
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (discountToDelete.value) {
    // Fermer la modal AVANT de lancer la requête pour éviter que le toast
    // apparaisse derrière la fenêtre de confirmation
    const id = discountToDelete.value.id
    showDeleteModal.value = false
    try {
      await deleteDiscount(id)
      discountToDelete.value = null
      // Notification de succès (toast vert)
      notifySuccess('Remise supprimée avec succès')
    } catch (err) {
      // La modal reste fermée; informer l'utilisateur via un toast rouge
      notifyError('Échec de la suppression de la remise')
    }
  }
}

const handleToggleDiscountStatus = async (row: Discount) => {
  // Ouvrir la modal de confirmation au lieu de basculer directement
  discountToToggle.value = row
  showToggleModal.value = true
}

const confirmToggle = async () => {
  if (!discountToToggle.value) return
  const id = discountToToggle.value.id
  const wasActive = discountToToggle.value.actif === 'activé'
  try {
    await toggleDiscountStatus(id)
    showToggleModal.value = false
    // Message utilisateur
    if (wasActive) {
      notifySuccess('Remise annulée')
    } else {
      notifySuccess('Remise appliquée')
    }
  } catch (err) {
    notifyError('Erreur lors du changement de statut')
  }
}


// Lifecycle
onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  fetchDiscounts()
  checkInitialGlobalPromotion()
})
</script>

<style scoped>
/* Masquer les flèches (spinners) des inputs de type number */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}
</style>